import TableRow from "@material-ui/core/TableRow";
import { ContentCell } from "../../common/material-table";
import { Checkbox, CircularProgress, TableCell } from "@material-ui/core";
import { DateDisplay } from "../../common/date-display/DateDisplay";
import { DateFormats } from "../../common/enums/DateFormat";
import { makeStyles } from "@material-ui/core/styles";
import { CourseType, MedicationState, ProductStatus } from "../../../services/medicationmanagement/medications";
import { MedicationCourse, MedicationExpiry, MedicationType } from "@communicare/medications/Contracts/MedicationModels";
import { ComplementaryBadge, CriticalBadge, NewMedBadge, PhoneOrderBadge } from "../../common/status-badge/StatusBadge";
import { StandardPharmacyMedicationCard } from '../pharmacy-medication-card/PharmacyMedicationCard';
import { MedicationStateTextStyle } from "../MedicationStateTextStyle";
import { SelectableMedicationStatusBadge } from "../SelectableMedicationStatusBadge";
import ReportProblemRoundedIcon from '@material-ui/icons/ReportProblemRounded';
import ErrorRoundedIcon from '@material-ui/icons/ErrorRounded';
import { RenderIf } from "../../common/render-if/RenderIf";
import {SelectableMedication} from "../usePharmacyMedications";
import {MedicationTypeEnum} from "../../../services/medicationmanagement/medications/models/MedicationTypeEnum"
import { QrModal } from "../scid-qr-code/QrModal";
import { getApiConfig } from "../../../config/api.config";

const CourseTypeMap = new Map<CourseType, MedicationCourse>();
CourseTypeMap.set(CourseType.AsRequired, MedicationCourse.AsRequired);
CourseTypeMap.set(CourseType.ShortCourse, MedicationCourse.ShortCourse);
CourseTypeMap.set(CourseType.Regular, MedicationCourse.Regular);

let useStyles = makeStyles({
    hover: (props: any) => ({
        cursor: props.selectable ? 'pointer' : 'default',
    }),
    icons: {
        position: "relative",
        marginRight: "12px",
        alignItems: "center"
    },
    statusIcon: {
        position: "absolute",
        bottom: "1px",
        right: "0",

        "&.nudge": {
            bottom: "-4px",
            right: "-3px"
        }
    },
    warningIcon: {
        color: "#f5a623", fontSize: 15, marginRight: 5, marginBottom: -3
    },
    errorIcon: { color: "#c53535", fontSize: 15, marginRight: 5, marginBottom: -3 }
});

const MedicationStatusBadge = (props: { medicationState: MedicationState }) => {
    switch (props.medicationState) {
        case MedicationState.New:
            return <NewMedBadge>
                New
            </NewMedBadge>;
         case MedicationState.RePrescribed:
            return <NewMedBadge>
                Re-prescribed
            </NewMedBadge>;    
        case MedicationState.Ceased:
            return <CriticalBadge>
                Ceased
            </CriticalBadge>;
        case MedicationState.Existing:
            return null;
        case MedicationState.Expired:
            return null;

    }
};

const ScriptModel = (props: { medication: SelectableMedication }) => {
    const { scriptNumber, startDate, product } = props.medication;
    const flag = getApiConfig().featureFlags?.qrcode;
    return <QrModal 
        scriptNumber={ scriptNumber }
        productName={ product.name }
        prescriptionDate={ startDate }
        flag ={ flag } 
        />
}


const CellContentStyle = (props: { medicationState: MedicationState; stateConfirmed:boolean; children: any }) => {
    return <MedicationStateTextStyle medicationState={props.medicationState}  stateConfirmed= {props.stateConfirmed}
    >
        {props.children}
    </MedicationStateTextStyle>;
};

export const ContentRow = (props: {
    item: SelectableMedication;
    isSelectable?: boolean;
    onActioned?: (medicationAuthoringSystem: string, medicationAuthoringSystemForeignKey: string) => void;
    renderStatusBadge?: (status: MedicationState) => JSX.Element;
    onRowSelected?: (authoringSystem: string, authoringSystemForeignKey: string) => void;
}) => {


    const classes = useStyles({ selectable: !props.isSelectable });
    return (
        <TableRow className={classes.hover} style={props.item.isNewUpdatedMedication ? { backgroundColor: "#fff8ed" } : {}} onClick={rowEvent => {
            let nodeName = (rowEvent.target as any).nodeName;
            if (nodeName === 'INPUT') {
                return;
            }
            rowEvent.stopPropagation();
            if (props.onRowSelected) {
                let { authoringSystem, authoringSystemForeignKey } = props.item;
                props.onRowSelected(authoringSystem, authoringSystemForeignKey);
            }
        }} hover role="body" tabIndex={-1}>
            {
                props.isSelectable ?
                    <TableCell padding="checkbox">
                        {(props.item as any).actioningInProgress ? <CircularProgress /> :
                            <Checkbox
                                color="primary"
                                inputProps={{ 'aria-labelledby': 'row' }}
                                checked={props.item.product?.action === ProductStatus.Actioned}
                                onChange={_ => {
                                    if (props.onActioned) {
                                        props.onActioned(props.item.authoringSystem, props.item.authoringSystemForeignKey)
                                    }
                                }}
                            />}
                    </TableCell> : null
            }

            <ContentCell>
                <CellContentStyle 
                    medicationState={props.item.status}
                    stateConfirmed = {props.item.confirmed}
                >
                    <StandardPharmacyMedicationCard item={props.item} />
                </CellContentStyle>
            </ContentCell>
            <ContentCell>
                <CellContentStyle 
                    medicationState={props.item.status}  
                    stateConfirmed = {props.item.confirmed}
                >{props.item.instructions}</CellContentStyle>
            </ContentCell>
            <ContentCell>
                <CellContentStyle 
                    medicationState={props.item.status} 
                    stateConfirmed = {props.item.confirmed}
                >                                 
                    {
                        props.item.medicationType === MedicationTypeEnum.ComplementaryTherapyOrder ?                        
                            <ComplementaryBadge></ComplementaryBadge>
                            : ( props.item.isOpenPhoneOrder ? <PhoneOrderBadge/>
                            : <ScriptModel medication={props.item}/> )
                    }
                </CellContentStyle>
            </ContentCell>
            <ContentCell>
                <CellContentStyle 
                    medicationState={props.item.status}  
                    stateConfirmed = {props.item.confirmed}
                >
                    <DateDisplay date={props.item.startDate} format={DateFormats.DDMMMYYYYHyphen} isTextBold={false} />
                </CellContentStyle>
            </ContentCell>
            <ContentCell>
                <CellContentStyle 
                    medicationState={props.item.status}  
                    stateConfirmed = {props.item.confirmed}
                >
                    <DateDisplay date={props.item.stoppedDate} format={DateFormats.DDMMMYYYYHyphen} isTextBold={false} />
                    <br />
                    {props.item.status !== MedicationState.Ceased &&
                        (props.item.medicationExpiryStatus === MedicationExpiry.Expired ?
                            <span style={{ color: "#c53535", fontSize: 14, fontWeight: 600 }}>
                                <ErrorRoundedIcon className={classes.errorIcon} />
                                {"Expired"}
                            </span>
                            : (props.item.medicationExpiryStatus === MedicationExpiry.AboutToExpire ?
                                <span style={{ color: "#925d07", fontSize: 14, fontWeight: 600 }}>
                                    <ReportProblemRoundedIcon className={classes.warningIcon} />
                                    {props.item.medicationExpiryDays} {props.item.medicationExpiryDays > 7 ? "weeks left" : 
                                        (props.item.medicationExpiryDays < 2 ? "day left" : "days left") 
                                    } </span> : <></>)
                        )
                    }
                </CellContentStyle>
            </ContentCell>
            <ContentCell>
                <CellContentStyle 
                    medicationState={props.item.status}  
                    stateConfirmed = {props.item.confirmed}
                >
                    {props.item.prescribedBy}
                </CellContentStyle>
            </ContentCell>
            <ContentCell>
                <RenderIf if={props.item.confirmed != true}>
                    {
                        props.isSelectable ?
                            <MedicationStatusBadge medicationState={props.item.status} /> :
                            <SelectableMedicationStatusBadge
                                medicationState={props.item.status}
                                checked={props.item.product?.action === ProductStatus.Actioned}
                                onChange={() => {
                                    if (props.onActioned) {
                                        props.onActioned(props.item.authoringSystem, props.item.authoringSystemForeignKey)
                                    }
                                }}
                            />

                    }
                </RenderIf>
            </ContentCell>
        </TableRow>

    );
}